import React, { useState } from 'react';
import clsx from 'clsx';
import { useTheme } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '@material-ui/icons/Menu';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { useStyles } from './PageDrawer.styles';
import { AppLeftMenu } from './AppLeftMenu';
import { AppAuthMenuContainer } from './AppAuthMenu';
import { Flex1 } from 'components/Flex1';
import { KeyweeLogo } from 'icons';
import { PageTitleContainer } from './PageTitle';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import InsertEmoticonIcon from '@material-ui/icons/InsertEmoticon';
import AddBoxIcon from '@material-ui/icons/AddBox';
import ListItemText from '@material-ui/core/ListItemText';
import List from '@material-ui/core/List';
import { EmojiSelectDialog } from '../../dialogs/EmojiSelectDialog';
import { Button, Popover } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

interface Props {
  children?: React.ReactNode;
}

export const PageDrawer: React.FC = ({ children }: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  const [open, setOpen] = React.useState(false);
  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  const [emoji, toggleEmoji] = useState(false);

  //
  // New Campaign
  //

  const [newCampaignAnchorEl, setNewCampaignAnchorEl] = useState<null | HTMLElement>(null);

  const handleNewCampaignClick = (event: React.MouseEvent<HTMLElement>) => {
    setNewCampaignAnchorEl(event.currentTarget);
  };

  const handleNewCampaignClose = () => {
    setNewCampaignAnchorEl(null);
  };

  const handleGoogleClick = () => {
    history.push('/campaigns/google/new');
    handleNewCampaignClose();
  };

  const handleFacebookClick = () => {
    history.push('/campaigns/facebook/new');
    handleNewCampaignClose();
  };

  const newCampaignOpen = Boolean(newCampaignAnchorEl);

  return (
    <div className={classes.root}>
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open
        })}
      >
        <Toolbar className={classes.mainToolbar}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            className={clsx(classes.menuButton, {
              [classes.hide]: open
            })}
            data-auto-id="top-button-1"
          >
            <MenuIcon />
          </IconButton>
          <PageTitleContainer />
          <Flex1 />
          {!open && <KeyweeLogo color="white" height={25} data-auto-id="top-logo-white" />}
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open
        })}
        classes={{
          paper: clsx({
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open
          })
        }}
      >
        <div className={classes.toolbar}>
          {open && (
            <div className={classes.logo}>
              <KeyweeLogo height={25} data-auto-id="top-logo-black" />
            </div>
          )}
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? (
              <ChevronRightIcon data-auto-id="chevron-right-icon" />
            ) : (
              <ChevronLeftIcon data-auto-id="chevron-left-icon" />
            )}
          </IconButton>
        </div>

        <Divider />

        <AppLeftMenu collapsed={!open} />

        <Flex1 />

        <List>
          <ListItem button data-auto-id="new-campaign-item" onClick={handleNewCampaignClick}>
            <ListItemIcon>
              <AddBoxIcon data-auto-id="add-box-icon" />
            </ListItemIcon>
            <ListItemText primary="New Campaign" />
          </ListItem>
          <ListItem button onClick={() => toggleEmoji((v) => !v)} data-auto-id="emoji-item">
            <ListItemIcon>
              <InsertEmoticonIcon data-auto-id="insert-emoticon-icon" />
            </ListItemIcon>
            <ListItemText primary="Emoji" />
          </ListItem>
        </List>

        <Divider />

        <AppAuthMenuContainer collapsed={!open} />
      </Drawer>
      <main className={classes.content}>
        <div className={classes.toolbar} />
        {children}
      </main>
      <EmojiSelectDialog open={emoji} handleToggle={() => toggleEmoji((v) => !v)} />
      <Popover
        open={newCampaignOpen}
        anchorEl={newCampaignAnchorEl}
        onClose={handleNewCampaignClose}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left'
        }}
      >
        <div className="p-2 flex flex-col items-start">
          <Button onClick={handleFacebookClick} size="small">
            New Facebook Campaign
          </Button>
          <Button onClick={handleGoogleClick} size="small">
            New Google Campaign
          </Button>
        </div>
      </Popover>
    </div>
  );
};
